import React, { forwardRef, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

const HIST_KEY_MAP = {
  'Australian Equities':    'hist_australian_equities',
  'International Equities': 'hist_international_equities',
  'Real Estate':            'hist_real_estate',
  'Infrastructure':         'hist_infrastructure',
  'Global Alpha':           'hist_global_alpha',
  'Corporate Debt':         'hist_corporate_debt',
  'Government Bonds':       'hist_government_bonds',
  'Cash':                   'hist_cash',
  'Commodities':            'hist_commodities',
  'Private Equity':         'hist_private_equity',
  'Private Debt':           'hist_private_debt',
  'Private Infrastructure': 'hist_private_infrastructure',
};

const DEFAULT_COLORS = {
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Global Alpha': '#BBCBD7',
  'Corporate Debt': '#AA8090',
  'Government Bonds': '#F3BD48',
  'Cash': '#EFE6D5',
  'Commodities': '#4B3219',
  'Private Equity': '#6C757D',
  'Private Debt': '#874564',
  'Private Infrastructure': '#12706C',
};

const FALLBACK_PALETTE = [
  '#072040', '#599BE3', '#f0a500', '#91CF9B', '#39AEC5',
  '#2f5596', '#E7E6E6', '#c0c0c0', '#d4a574', '#8B5CF6',
];

const DISPLAY_ORDER = [
  'Australian Equities',
  'International Equities',
  'Real Estate',
  'Infrastructure',
  'Global Alpha',
  'Corporate Debt',
  'Government Bonds',
  'Cash',
  'Commodities',
  'Private Equity',
  'Private Debt',
  'Private Infrastructure',
];

const getColor = (assetClass, index, colours) => {
  const key = HIST_KEY_MAP[assetClass];
  if (key && colours[key]) return colours[key];
  return DEFAULT_COLORS[assetClass] || FALLBACK_PALETTE[index % 10];
};

const HistoricalAllocationChart = forwardRef(({ data, colours = {} }, ref) => {
  if (!data || !data.dates || data.dates.length === 0) {
    return <div className="chart-placeholder">No historical allocation data available</div>;
  }

  const yearLabels = useMemo(() => {
    const years = data.dates.map(d => d.substring(0, 4));
    const labelMap = {};
    let lastYear = null;
    let lastIndex = null;
    years.forEach((y, i) => {
      if (y !== lastYear) {
        if (lastIndex !== null && i - lastIndex < 4) {
          delete labelMap[lastIndex];
        }
        labelMap[i] = y;
        lastYear = y;
        lastIndex = i;
      }
    });
    return labelMap;
  }, [data.dates]);

  const chartData = useMemo(() => {
    // Merge EM Equities into International Equities
    const mergedSeries = {};
    Object.entries(data.series).forEach(([ac, values]) => {
      const name = ac === 'EM Equities' ? 'International Equities' : ac;
      if (mergedSeries[name]) {
        mergedSeries[name] = mergedSeries[name].map((v, i) => (v || 0) + (values[i] || 0));
      } else {
        mergedSeries[name] = [...values];
      }
    });
    const assetClasses = Object.keys(mergedSeries).sort((a, b) => {
      const idxA = DISPLAY_ORDER.indexOf(a);
      const idxB = DISPLAY_ORDER.indexOf(b);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
    return {
      labels: data.dates,
      datasets: assetClasses.map((ac, i) => ({
        label: ac,
        data: mergedSeries[ac],
        backgroundColor: getColor(ac, i, colours),
        borderColor: getColor(ac, i, colours),
        borderWidth: 0.5,
      })),
    };
  }, [data, colours]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: 10 }, boxWidth: 20, boxHeight: 2, padding: 5 },
      },
      tooltip: {
        mode: 'index',
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            return data.dates[items[0].dataIndex] || '';
          },
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        border: { display: false },
        grid: { display: false },
        ticks: {
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: false,
          callback: function (val, index) {
            return yearLabels[index] || '';
          },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.1)' },
        ticks: {
          font: { size: 10 },
          callback: (val) => `${val}%`,
        },
      },
    },
  }), [data.dates, yearLabels]);

  return (
    <div style={{ height: '260px', margin: 0 }}>
      <Bar ref={ref} data={chartData} options={options} />
    </div>
  );
});

HistoricalAllocationChart.displayName = 'HistoricalAllocationChart';
export default HistoricalAllocationChart;
